import React, { useState, useEffect, useRef, Component, Suspense, lazy } from 'react';
import * as Sentry from '@sentry/react';
import useStore from './stores/useStore';
import { useIsMobile } from './hooks/useIsMobile';
import DemoLauncher from './components/DemoLauncher';
import LoginScreen from './components/LoginScreen';
import ConnectivityIndicator from './components/ConnectivityIndicator';
import BetaFeedbackIndicator from './components/BetaFeedbackIndicator';
import EmberModal from './components/EmberModal';

const TableMap = lazy(() => import('./components/TableMap'));
const OrderEntry = lazy(() => import('./components/OrderEntry'));
const ManagerDashboard = lazy(() => import('./components/ManagerDashboard'));
const TimeClock = lazy(() => import('./components/TimeClock'));
const KitchenDisplay = lazy(() => import('./components/KitchenDisplay'));
const BarDisplay = lazy(() => import('./components/BarDisplay'));
const HostStation = lazy(() => import('./components/HostStation'));
const TipViewer = lazy(() => import('./components/TipViewer'));
const InventoryManagement = lazy(() => import('./components/InventoryManagement'));
const LocationManagement = lazy(() => import('./components/LocationManagement'));
const ScheduleHub = lazy(() => import('./components/ScheduleHub'));

// Skip navigation link — WCAG 2.4.1
function SkipNav() {
    return (
        <a
            href="#main-content"
            className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-[9999] focus:bg-ember-600 focus:text-white focus:px-4 focus:py-2 focus:rounded focus:font-semibold focus:shadow-lg focus:outline-none focus:ring-2 focus:ring-white"
        >
            Skip to main content
        </a>
    );
}

// DRY Ember floating button — shared across all authenticated views
function EmberButton({ onClick }) {
    return (
        <button
            onClick={onClick}
            aria-label="Open Ember AI Assistant"
            title="Ask Ember"
            className="fixed bottom-6 right-6 w-14 h-14 bg-purple-600 hover:bg-purple-500 text-white rounded-full shadow-2xl flex items-center justify-center text-2xl z-40 transition-all hover:scale-110 active:scale-95 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-purple-600"
        >
            <span aria-hidden="true">🤖</span>
        </button>
    );
}

// Error Boundary to catch and display React errors
export class ErrorBoundary extends Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, error: null, errorInfo: null };
    }

    static getDerivedStateFromError(error) {
        return { hasError: true, error };
    }

    componentDidCatch(error, errorInfo) {
        console.error(`[ErrorBoundary${this.props.section ? ` - ${this.props.section}` : ''}]`, error, errorInfo);
        Sentry.captureException(error, {
            tags: {
                boundary: 'app',
                section: this.props.section || 'unknown',
            },
            contexts: {
                react: {
                    componentStack: errorInfo?.componentStack,
                },
            },
        });
        this.setState({ errorInfo });
    }

    handleRetry = () => {
        this.setState({ hasError: false, error: null, errorInfo: null });
    }

    render() {
        if (this.state.hasError) {
            return (
                <div className={`${this.props.section ? 'p-8' : 'h-full'} flex items-center justify-center bg-red-900 text-white p-8`} role="alert">
                    <div className="max-w-2xl">
                        <h1 className="text-3xl font-bold mb-4">
                            {this.props.section ? `${this.props.section} Error` : 'Something went wrong'}
                        </h1>
                        <p className="text-red-200 mb-4">
                            {this.props.section
                                ? `The ${this.props.section} section encountered an error. Your orders are safe.`
                                : 'An unexpected error occurred.'}
                        </p>
                        <div className="bg-red-800 rounded-lg p-4 mb-4">
                            <p className="font-mono text-sm break-all">
                                {this.state.error?.toString()}
                            </p>
                        </div>
                        {this.state.errorInfo && (
                            <details className="bg-red-800 rounded-lg p-4 mb-4">
                                <summary className="cursor-pointer font-bold">Stack trace</summary>
                                <pre className="mt-2 text-xs overflow-auto max-h-96 whitespace-pre-wrap">
                                    {this.state.errorInfo.componentStack}
                                </pre>
                            </details>
                        )}
                        <div className="flex gap-3">
                            <button
                                onClick={this.handleRetry}
                                className="bg-orange-500 hover:bg-orange-400 text-white px-6 py-2 rounded font-bold transition-colors focus:outline-none focus:ring-2 focus:ring-white"
                            >
                                Try Again
                            </button>
                            <button
                                onClick={() => window.location.reload()}
                                className="bg-white text-red-900 px-6 py-2 rounded font-bold focus:outline-none focus:ring-2 focus:ring-red-900"
                            >
                                Reload Page
                            </button>
                        </div>
                    </div>
                </div>
            );
        }
        return this.props.children;
    }
}

const VALID_VIEWS = new Set([
    'launcher',
    'login',
    'map',
    'order',
    'manager',
    'timeclock',
    'kds',
    'bar',
    'host',
    'tips',
    'inventory',
    'locations',
    'schedule'
]);

function ToastContainer({ notifications, removeNotification }) {
    return (
        <div
            className="fixed toast-safe-top right-4 z-[100] space-y-2 pointer-events-none"
            aria-live="polite"
            aria-atomic="true"
            role="region"
            aria-label="Notifications"
        >
            {notifications.map((notification) => (
                <div
                    key={notification.id}
                    className={`
                        pointer-events-auto px-6 py-4 rounded-lg shadow-2xl
                        transform transition-all duration-300 ease-out
                        animate-slide-in-right flex items-center gap-3 min-w-[300px]
                        ${notification.type === 'success' ? 'bg-green-600 text-white' : ''}
                        ${notification.type === 'error' ? 'bg-red-600 text-white' : ''}
                        ${notification.type === 'warning' ? 'bg-yellow-500 text-black' : ''}
                        ${notification.type === 'info' ? 'bg-blue-600 text-white' : ''}
                    `}
                    role="alert"
                >
                    <span className="text-xl" aria-hidden="true">
                        {notification.type === 'success' && '\u2713'}
                        {notification.type === 'error' && '\u2715'}
                        {notification.type === 'warning' && '\u26A0'}
                        {notification.type === 'info' && '\u2139'}
                    </span>
                    <span className="flex-1 font-medium">{notification.message}</span>
                    <button
                        onClick={() => removeNotification(notification.id)}
                        className="opacity-70 hover:opacity-100 transition-opacity focus:outline-none focus:ring-2 focus:ring-white rounded"
                        aria-label="Dismiss notification"
                    >
                        <span aria-hidden="true">&times;</span>
                    </button>
                </div>
            ))}
        </div>
    );
}

function ViewLoader({ label }) {
    return (
        <div
            className="min-h-full bg-gray-900 flex items-center justify-center text-white"
            role="status"
            aria-busy="true"
            aria-label={`${label} loading`}
        >
            <div className="flex items-center gap-3">
                <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-red-500" aria-hidden="true"></div>
                <span className="text-lg">{label} loading...</span>
            </div>
        </div>
    );
}

const MOBILE_TABS = [
    { id: 'map', label: 'Tables', icon: '\u{1F5FA}' },
    { id: 'order', label: 'Order', icon: '\u{1F37D}\uFE0F' },
    { id: 'kds', label: 'Kitchen', icon: '\u{1F525}' },
    { id: 'more', label: 'More', icon: '\u22EF' },
];

const MORE_ITEMS = [
    { id: 'manager', label: 'Manager', icon: '\u{1F4CA}' },
    { id: 'tips', label: 'Tips', icon: '\u{1F4B5}' },
    { id: 'timeclock', label: 'Time Clock', icon: '\u23F0' },
    { id: 'inventory', label: 'Inventory', icon: '\u{1F4E6}' },
    { id: 'host', label: 'Host', icon: '\u{1F4CB}' },
    { id: 'bar', label: 'Bar', icon: '\u{1F378}' },
    { id: 'schedule', label: 'Schedule', icon: '\u{1F4C5}' },
];

function MobileTabBar({ currentView, onNavigate, hasSelectedTable }) {
    const [showMore, setShowMore] = React.useState(false);

    return (
        <>
            {/* More menu overlay */}
            {showMore && (
                <div
                    className="fixed inset-0 z-30"
                    role="presentation"
                    onClick={() => setShowMore(false)}
                >
                    <div
                        className="mobile-more-menu absolute left-0 right-0 bg-smoke-800 border-t border-smoke-700 p-3 animate-slide-in-bottom"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div
                            className="grid grid-cols-3 gap-3"
                            role="menu"
                            aria-label="More navigation options"
                        >
                            {MORE_ITEMS.map(item => (
                                <button
                                    key={item.id}
                                    role="menuitem"
                                    onClick={() => { onNavigate(item.id); setShowMore(false); }}
                                    aria-label={item.label}
                                    aria-current={currentView === item.id ? 'page' : undefined}
                                    className={`flex flex-col items-center gap-1 py-3 px-2 rounded-xl transition-colors ${
                                        currentView === item.id ? 'bg-ember-500/20 text-ember-400' : 'text-smoke-400 hover:bg-smoke-700'
                                    }`}
                                >
                                    <span className="text-2xl" aria-hidden="true">{item.icon}</span>
                                    <span className="text-xs font-medium">{item.label}</span>
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* Tab bar */}
            <nav className="mobile-tab-bar flex" role="tablist" aria-label="Main navigation">
                {MOBILE_TABS.map(tab => {
                    const isActive = tab.id === 'more'
                        ? MORE_ITEMS.some(m => m.id === currentView)
                        : currentView === tab.id;

                    return (
                        <button
                            key={tab.id}
                            onClick={() => {
                                if (tab.id === 'more') {
                                    setShowMore(!showMore);
                                } else if (tab.id === 'order' && !hasSelectedTable) {
                                    setShowMore(false);
                                    onNavigate('map'); // must select a table first
                                } else {
                                    setShowMore(false);
                                    onNavigate(tab.id);
                                }
                            }}
                            role="tab"
                            aria-selected={isActive}
                            aria-label={tab.id === 'more' ? 'More navigation options' : tab.label}
                            className={`flex-1 flex flex-col items-center gap-0.5 py-2 transition-colors focus:outline-none focus:ring-2 focus:ring-inset focus:ring-ember-400 ${
                                isActive ? 'text-ember-400' : 'text-smoke-500'
                            }`}
                        >
                            <span className="text-xl" aria-hidden="true">{tab.icon}</span>
                            <span className="text-[10px] font-medium">{tab.label}</span>
                        </button>
                    );
                })}
            </nav>
        </>
    );
}

function App() {
    const {
        view,
        setView,
        currentUser,
        isAuthenticated,
        logout,
        selectedTable,
        selectTable,
        clearSelectedTable,
        notifications,
        removeNotification
    } = useStore();

    const { isMobile } = useIsMobile();
    const showMobileNav = isMobile && isAuthenticated && !['launcher', 'login'].includes(view);

    const [emberOpen, setEmberOpen] = useState(false);

    const viewRef = useRef(view);
    const historyReadyRef = useRef(false);
    const skipHistoryRef = useRef(false);

    useEffect(() => {
        viewRef.current = view;
    }, [view]);

    useEffect(() => {
        if (typeof window === 'undefined') return;

        const stateView = window.history.state?.view;
        const hashView = window.location.hash?.replace('#', '');
        const initialView =
            (VALID_VIEWS.has(stateView) && stateView) ||
            (VALID_VIEWS.has(hashView) && hashView) ||
            viewRef.current;

        if (initialView !== viewRef.current) {
            skipHistoryRef.current = true;
            setView(initialView);
        }

        window.history.replaceState({ view: initialView }, '', `#${initialView}`);
        historyReadyRef.current = true;

        const handlePopState = (event) => {
            let nextView = VALID_VIEWS.has(event.state?.view) ? event.state.view : null;

            // Fallback to URL hash if state is null
            if (!nextView) {
                const hashView = window.location.hash.slice(1);
                if (VALID_VIEWS.has(hashView)) {
                    nextView = hashView;
                }
            }

            if (nextView && nextView !== viewRef.current) {
                skipHistoryRef.current = true;
                setView(nextView);
            }
        };

        window.addEventListener('popstate', handlePopState);
        return () => window.removeEventListener('popstate', handlePopState);
    }, [setView]);

    useEffect(() => {
        if (typeof window === 'undefined' || !historyReadyRef.current) return;
        if (skipHistoryRef.current) {
            skipHistoryRef.current = false;
            return;
        }
        window.history.pushState({ view }, '', `#${view}`);
    }, [view]);

    // Get role-based default view
    const getDefaultViewForRole = (role) => {
        switch (role) {
            case 'manager':
            case 'admin':
                return 'manager';
            case 'kitchen':
                return 'kds';
            case 'host':
                return 'map';
            case 'server':
            case 'bartender':
            default:
                return 'map';
        }
    };

    const handleDemoSelect = (demoId) => {
        if (demoId === 'pos') {
            setView('login');
        } else if (demoId === 'kds') {
            setView('kds');
        } else if (demoId === 'bar') {
            setView('bar');
        } else if (demoId === 'host') {
            setView('host');
        } else if (demoId === 'manager') {
            setView('manager');
        } else if (demoId === 'timeclock') {
            setView('timeclock');
        } else if (demoId === 'tips') {
            setView('tips');
        } else if (demoId === 'inventory') {
            setView('inventory');
        } else if (demoId === 'locations') {
            setView('locations');
        } else if (demoId === 'schedule') {
            setView('schedule');
        }
    };

    const handleLogin = (user) => {
        // Navigate to role-appropriate view
        const defaultView = getDefaultViewForRole(user.role);
        setView(defaultView);
    };

    const handleLogout = () => {
        logout();
        clearSelectedTable();
        setView('launcher');
    };

    const handleTableSelect = (tableId) => {
        selectTable(tableId);
        setView('order');
    };

    const handleBackFromOrder = () => {
        clearSelectedTable();
        setView('map');
    };

    // Demo Launcher
    if (view === 'launcher') {
        return (
            <>
                <SkipNav />
                <main id="main-content">
                    <DemoLauncher onSelectView={handleDemoSelect} />
                </main>
                <ConnectivityIndicator />
                {import.meta.env.DEV && <BetaFeedbackIndicator project="fire-pos" />}
                <ToastContainer
                    notifications={notifications}
                    removeNotification={removeNotification}
                />
            </>
        );
    }

    // POS Flow - Login
    if (view === 'login') {
        return (
            <>
                <SkipNav />
                <main id="main-content">
                    <LoginScreen onLogin={handleLogin} />
                </main>
                <ConnectivityIndicator />
                {import.meta.env.DEV && <BetaFeedbackIndicator project="fire-pos" />}
                <ToastContainer
                    notifications={notifications}
                    removeNotification={removeNotification}
                />
            </>
        );
    }

    // POS Flow - Table Map
    if (view === 'map') {
        return (
            <>
                <SkipNav />
                <main id="main-content" className={showMobileNav ? 'mobile-tab-bar-clearance' : ''}>
                    <Suspense fallback={<ViewLoader label="Table map" />}>
                        <TableMap
                            onTableSelect={handleTableSelect}
                            onLogout={handleLogout}
                            onManagerClick={() => setView('manager')}
                            user={currentUser}
                        />
                    </Suspense>
                </main>
                {showMobileNav && <MobileTabBar currentView={view} onNavigate={setView} hasSelectedTable={!!selectedTable} />}
                <ConnectivityIndicator />
                {import.meta.env.DEV && <BetaFeedbackIndicator project="fire-pos" />}
                <ToastContainer
                    notifications={notifications}
                    removeNotification={removeNotification}
                />
                <EmberButton onClick={() => setEmberOpen(true)} />
                <EmberModal isOpen={emberOpen} onClose={() => setEmberOpen(false)} />
            </>
        );
    }

    // POS Flow - Order Entry
    if (view === 'order') {
        return (
            <>
                <SkipNav />
                <main id="main-content" className={showMobileNav ? 'mobile-tab-bar-clearance' : ''}>
                    <ErrorBoundary section="Order Entry">
                        <Suspense fallback={<ViewLoader label="Order entry" />}>
                            <OrderEntry
                                tableId={selectedTable?.number || selectedTable?.id}
                                table={selectedTable}
                                onBack={handleBackFromOrder}
                                user={currentUser}
                            />
                        </Suspense>
                    </ErrorBoundary>
                </main>
                {showMobileNav && <MobileTabBar currentView={view} onNavigate={setView} hasSelectedTable={!!selectedTable} />}
                <ConnectivityIndicator />
                {import.meta.env.DEV && <BetaFeedbackIndicator project="fire-pos" />}
                <ToastContainer
                    notifications={notifications}
                    removeNotification={removeNotification}
                />
                <EmberButton onClick={() => setEmberOpen(true)} />
                <EmberModal isOpen={emberOpen} onClose={() => setEmberOpen(false)} />
            </>
        );
    }

    // Manager Dashboard
    if (view === 'manager') {
        return (
            <>
                <SkipNav />
                <main id="main-content" className={showMobileNav ? 'mobile-tab-bar-clearance' : ''}>
                    <ErrorBoundary section="Manager Dashboard">
                        <Suspense fallback={<ViewLoader label="Manager dashboard" />}>
                            <ManagerDashboard
                                onClose={() => setView(isAuthenticated ? 'map' : 'launcher')}
                                onLocations={() => setView('locations')}
                                user={currentUser}
                            />
                        </Suspense>
                    </ErrorBoundary>
                </main>
                {showMobileNav && <MobileTabBar currentView={view} onNavigate={setView} hasSelectedTable={!!selectedTable} />}
                <ConnectivityIndicator />
                {import.meta.env.DEV && <BetaFeedbackIndicator project="fire-pos" />}
                <ToastContainer
                    notifications={notifications}
                    removeNotification={removeNotification}
                />
                <EmberButton onClick={() => setEmberOpen(true)} />
                <EmberModal isOpen={emberOpen} onClose={() => setEmberOpen(false)} />
            </>
        );
    }

    // Time Clock
    if (view === 'timeclock') {
        return (
            <>
                <SkipNav />
                <main id="main-content" className={showMobileNav ? 'mobile-tab-bar-clearance' : ''}>
                    <Suspense fallback={<ViewLoader label="Time clock" />}>
                        <TimeClock
                            onClose={() => setView(isAuthenticated ? 'map' : 'launcher')}
                            user={currentUser}
                        />
                    </Suspense>
                </main>
                {showMobileNav && <MobileTabBar currentView={view} onNavigate={setView} hasSelectedTable={!!selectedTable} />}
                <ConnectivityIndicator />
                {import.meta.env.DEV && <BetaFeedbackIndicator project="fire-pos" />}
                <ToastContainer
                    notifications={notifications}
                    removeNotification={removeNotification}
                />
                <EmberButton onClick={() => setEmberOpen(true)} />
                <EmberModal isOpen={emberOpen} onClose={() => setEmberOpen(false)} />
            </>
        );
    }

    // Kitchen Display System
    if (view === 'kds') {
        return (
            <>
                <SkipNav />
                <main id="main-content" className={showMobileNav ? 'mobile-tab-bar-clearance' : ''}>
                    <ErrorBoundary section="Kitchen Display">
                        <Suspense fallback={<ViewLoader label="Kitchen display" />}>
                            <KitchenDisplay
                                onClose={() => setView(isAuthenticated ? 'map' : 'launcher')}
                                user={currentUser}
                            />
                        </Suspense>
                    </ErrorBoundary>
                </main>
                {showMobileNav && <MobileTabBar currentView={view} onNavigate={setView} hasSelectedTable={!!selectedTable} />}
                <ConnectivityIndicator />
                {import.meta.env.DEV && <BetaFeedbackIndicator project="fire-pos" />}
                <ToastContainer
                    notifications={notifications}
                    removeNotification={removeNotification}
                />
                <EmberButton onClick={() => setEmberOpen(true)} />
                <EmberModal isOpen={emberOpen} onClose={() => setEmberOpen(false)} />
            </>
        );
    }

    // Bar Display System
    if (view === 'bar') {
        return (
            <>
                <SkipNav />
                <main id="main-content" className={showMobileNav ? 'mobile-tab-bar-clearance' : ''}>
                    <Suspense fallback={<ViewLoader label="Bar display" />}>
                        <BarDisplay
                            onClose={() => setView(isAuthenticated ? 'map' : 'launcher')}
                            user={currentUser}
                        />
                    </Suspense>
                </main>
                {showMobileNav && <MobileTabBar currentView={view} onNavigate={setView} hasSelectedTable={!!selectedTable} />}
                <ConnectivityIndicator />
                {import.meta.env.DEV && <BetaFeedbackIndicator project="fire-pos" />}
                <ToastContainer
                    notifications={notifications}
                    removeNotification={removeNotification}
                />
                <EmberButton onClick={() => setEmberOpen(true)} />
                <EmberModal isOpen={emberOpen} onClose={() => setEmberOpen(false)} />
            </>
        );
    }

    // Host Station
    if (view === 'host') {
        return (
            <>
                <SkipNav />
                <main id="main-content" className={showMobileNav ? 'mobile-tab-bar-clearance' : ''}>
                    <Suspense fallback={<ViewLoader label="Host station" />}>
                        <HostStation
                            onClose={() => setView(isAuthenticated ? 'map' : 'launcher')}
                            user={currentUser}
                        />
                    </Suspense>
                </main>
                {showMobileNav && <MobileTabBar currentView={view} onNavigate={setView} hasSelectedTable={!!selectedTable} />}
                <ConnectivityIndicator />
                {import.meta.env.DEV && <BetaFeedbackIndicator project="fire-pos" />}
                <ToastContainer
                    notifications={notifications}
                    removeNotification={removeNotification}
                />
                <EmberButton onClick={() => setEmberOpen(true)} />
                <EmberModal isOpen={emberOpen} onClose={() => setEmberOpen(false)} />
            </>
        );
    }

    // Tip Viewer
    if (view === 'tips') {
        return (
            <>
                <SkipNav />
                <main id="main-content" className={showMobileNav ? 'mobile-tab-bar-clearance' : ''}>
                    <Suspense fallback={<ViewLoader label="Tip viewer" />}>
                        <TipViewer
                            employeeId={currentUser?.id || 'demo'}
                            onClose={() => setView(isAuthenticated ? 'map' : 'launcher')}
                            user={currentUser}
                        />
                    </Suspense>
                </main>
                {showMobileNav && <MobileTabBar currentView={view} onNavigate={setView} hasSelectedTable={!!selectedTable} />}
                <ConnectivityIndicator />
                {import.meta.env.DEV && <BetaFeedbackIndicator project="fire-pos" />}
                <ToastContainer
                    notifications={notifications}
                    removeNotification={removeNotification}
                />
                <EmberButton onClick={() => setEmberOpen(true)} />
                <EmberModal isOpen={emberOpen} onClose={() => setEmberOpen(false)} />
            </>
        );
    }

    // Inventory Management
    if (view === 'inventory') {
        return (
            <>
                <SkipNav />
                <main id="main-content" className={showMobileNav ? 'mobile-tab-bar-clearance' : ''}>
                    <ErrorBoundary section="Inventory">
                        <Suspense fallback={<ViewLoader label="Inventory" />}>
                            <InventoryManagement
                                onClose={() => setView(isAuthenticated ? 'manager' : 'launcher')}
                                user={currentUser}
                            />
                        </Suspense>
                    </ErrorBoundary>
                </main>
                {showMobileNav && <MobileTabBar currentView={view} onNavigate={setView} hasSelectedTable={!!selectedTable} />}
                <ConnectivityIndicator />
                {import.meta.env.DEV && <BetaFeedbackIndicator project="fire-pos" />}
                <ToastContainer
                    notifications={notifications}
                    removeNotification={removeNotification}
                />
                <EmberButton onClick={() => setEmberOpen(true)} />
                <EmberModal isOpen={emberOpen} onClose={() => setEmberOpen(false)} />
            </>
        );
    }

    // Location Management (Admin)
    if (view === 'locations') {
        return (
            <>
                <SkipNav />
                <main id="main-content" className={showMobileNav ? 'mobile-tab-bar-clearance' : ''}>
                    <ErrorBoundary section="Location Management">
                        <Suspense fallback={<ViewLoader label="Location management" />}>
                            <LocationManagement
                                onClose={() => setView(isAuthenticated ? 'manager' : 'launcher')}
                            />
                        </Suspense>
                    </ErrorBoundary>
                </main>
                {showMobileNav && <MobileTabBar currentView={view} onNavigate={setView} hasSelectedTable={!!selectedTable} />}
                <ConnectivityIndicator />
                {import.meta.env.DEV && <BetaFeedbackIndicator project="fire-pos" />}
                <ToastContainer
                    notifications={notifications}
                    removeNotification={removeNotification}
                />
                <EmberButton onClick={() => setEmberOpen(true)} />
                <EmberModal isOpen={emberOpen} onClose={() => setEmberOpen(false)} />
            </>
        );
    }

    // Schedule Hub
    if (view === 'schedule') {
        return (
            <>
                <SkipNav />
                <main id="main-content" className={showMobileNav ? 'mobile-tab-bar-clearance' : ''}>
                    <ErrorBoundary section="Schedule Hub">
                        <Suspense fallback={<ViewLoader label="Schedule hub" />}>
                            <ScheduleHub
                                onClose={() => setView(isAuthenticated ? 'manager' : 'launcher')}
                                user={currentUser}
                            />
                        </Suspense>
                    </ErrorBoundary>
                </main>
                {showMobileNav && <MobileTabBar currentView={view} onNavigate={setView} hasSelectedTable={!!selectedTable} />}
                <ConnectivityIndicator />
                {import.meta.env.DEV && <BetaFeedbackIndicator project="fire-pos" />}
                <ToastContainer
                    notifications={notifications}
                    removeNotification={removeNotification}
                />
                <EmberButton onClick={() => setEmberOpen(true)} />
                <EmberModal isOpen={emberOpen} onClose={() => setEmberOpen(false)} />
            </>
        );
    }

    // Fallback
    return (
        <>
            <SkipNav />
            <main id="main-content">
                <DemoLauncher onSelectView={handleDemoSelect} />
            </main>
            <ConnectivityIndicator />
            <ToastContainer
                notifications={notifications}
                removeNotification={removeNotification}
            />
        </>
    );
}

export default App;
